import React, { useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { proDesignTokens } from './ProDesignTokens';
import { logAnalyticsEvent } from '../../utils/analytics';
import { color, r } from '../../theme/tokens';

// Styled components for Apple-inspired minimalist design
const ProCard = styled(Card)(({ theme }) => ({
  background: color.paper,
  border: `${r(1)} solid ${proDesignTokens.colors.border.subtle}`,
  borderRadius: proDesignTokens.borderRadius.lg,
  boxShadow: proDesignTokens.shadows.medium,
  transition: proDesignTokens.transitions.medium,
  position: 'relative',
  overflow: 'visible',
  '&:hover': {
    boxShadow: proDesignTokens.shadows.strong,
    transform: `translateY(-${r(2)})`,
    borderColor: proDesignTokens.colors.border.medium,
  }
}));

/**
 * UpgradePrompt component for encouraging Pro upgrades
 * 
 * @param {Object} props
 * @param {string} [props.feature] - The specific feature being promoted
 * @param {string} [props.description] - Custom description of the feature benefits
 * @param {string} [props.ctaText] - Custom call-to-action button text
 * @param {string} [props.size='medium'] - Size variant: 'small', 'medium', 'large'
 * @param {boolean} [props.showTrialInfo=true] - Whether to show trial information
 * @returns {React.ReactElement}
 */
function UpgradePrompt({ 
  feature = "Features",
  description = "Unlock research-backed stacks, medication support, and advanced analysis.",
  ctaText = "Start Free Trial",
  size = 'medium',
  showTrialInfo = true,
  // source identifies which context triggered the prompt (e.g. 'rate_limit', 'deep_dive', 'stack_generation')
  // used as a property on analytics events so we can distinguish prompt placements in Mixpanel
  source = 'unknown',
}) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isTrialing, subscriptionStatus, isLapsed, isComplimentaryExpired } = useFeatureAccess();

  // Fire when the prompt becomes visible with a known source.
  // Skipped for 'unknown' so untouched callsites don't pollute Mixpanel.
  // Deps include feature+source so re-renders with different context emit correctly.
  useEffect(() => {
    if (source === 'unknown') return;
    logAnalyticsEvent('upgrade_prompt_shown', { feature, source });
  }, [feature, source]);

  const handleUpgrade = async () => {
    if (source !== 'unknown') {
      logAnalyticsEvent('upgrade_prompt_clicked', { feature, source });
    }

    if (!currentUser) {
      navigate('/login');
      return;
    }

    navigate('/subscribe', {
      state: {
        from: { pathname: window.location.pathname },
        source,
      },
    });
  };

  // Different sizing options
  const sizing = {
    small: {
      cardSx: { maxWidth: 300 },
      titleVariant: 'h6',
      spacing: 1
    },
    medium: {
      cardSx: { maxWidth: 400 },
      titleVariant: 'h5',
      spacing: 2
    },
    large: {
      cardSx: { maxWidth: 500 },
      titleVariant: 'h4',
      spacing: 3
    }
  };

  const currentSizing = sizing[size] || sizing.medium;

  // Customize CTA based on subscription status
  const getCtaText = () => {
    if (isTrialing) {
      return 'Manage Subscription';
    }
    if (subscriptionStatus?.status === 'past_due') {
      return 'Update Payment';
    }
    if (isLapsed()) {
      return 'Restore Access';
    }
    // Former complimentary users never had a paid Membership, so restore /
    // reactivate language would be wrong (design §3.5).
    if (isComplimentaryExpired()) {
      return 'Start Membership';
    }
    return ctaText;
  };

  // Customize description based on user type
  const getDescription = () => {
    if (description) {
      return description; // Use provided description if available
    }

    if (isLapsed()) {
      return `Your trial has expired. You can restore full access including ${feature.toLowerCase()} anytime.`;
    }

    if (isComplimentaryExpired()) {
      return `Your complimentary access has ended. Unlock ${feature.toLowerCase()} with Membership.`;
    }

    // Default description for never-subscribed users
    return `Unlock ${feature.toLowerCase()} with enhanced AI intelligence.`;
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', my: currentSizing.spacing }}>
      <ProCard sx={currentSizing.cardSx}>
        <CardContent sx={{ p: currentSizing.spacing, textAlign: 'center' }}>
          {/* Badge removed per rebrand */}

          {/* Title */}
          <Typography 
            variant={currentSizing.titleVariant} 
            component="h2" 
            gutterBottom
            sx={{ 
              ...proDesignTokens.typography.proHeading,
              color: 'text.primary',
              mb: proDesignTokens.spacing.sm,
              fontSize: size === 'small' ? '1rem' : '1.125rem'
            }}
          >
            {feature}
          </Typography>

          {/* Description */}
          <Typography 
            variant="body2" 
            sx={{ 
              ...proDesignTokens.typography.proBody,
              color: proDesignTokens.colors.neutral[600],
              mb: currentSizing.spacing,
              lineHeight: 1.5,
            }}
          >
            {getDescription()}
          </Typography>

          {/* Trial Info */}
          {showTrialInfo && !isTrialing && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                mb: currentSizing.spacing,
                color: proDesignTokens.colors.neutral[500],
                fontSize: proDesignTokens.typography.proBadge.fontSize,
                fontWeight: 400
              }}
            >
              14-day free trial. Cancel anytime.
            </Typography>
          )}

          {/* CTA Button */}
          <Button
            variant="contained"
            size={size === 'small' ? 'small' : 'large'}
            onClick={handleUpgrade}
            sx={{
              background: proDesignTokens.colors.pro.accent,
              // Dark ink on the accent fill: white measures 3.5:1 there and
              // fails AA at body size (brief §9; the PR 280 correction).
              color: color.accentInk,
              fontWeight: 600,
              px: currentSizing.spacing * 1.5,
              py: size === 'small' ? 0.75 : 1,
              borderRadius: proDesignTokens.borderRadius.md,
              textTransform: 'none',
              transition: proDesignTokens.transitions.medium,
              '&:hover': {
                background: color.accentHover,
                transform: `translateY(-${r(1)})`,
                boxShadow: proDesignTokens.shadows.medium,
              },
              '&:disabled': {
                background: proDesignTokens.colors.neutral[300],
                color: proDesignTokens.colors.neutral[500],
              }
            }}
          >
            {getCtaText()}
          </Button>
        </CardContent>
      </ProCard>
    </Box>
  );
}

export default UpgradePrompt; 